import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { setupAdminInstance } from './components/api/axiosConfig';
import ChatWidget from './components/chat/ChatWidget';
import WorkflowViewer from './components/WorkflowViewer'; // Import the workflow viewer component

// Get settings from window with proper type checking
const getSettings = () => {
    return {
        ...(window.wpAiWorkflowsSettings || {}),
        apiUrl: window.wpAiWorkflowsSettings?.apiUrl || '/wp-json/wp-ai-workflows/v1',
        nonce: window.wpAiWorkflowsSettings?.nonce || '',
        siteUrl: window.wpAiWorkflowsSettings?.siteUrl || '',
        assetsUrl: window.wpAiWorkflowsSettings?.assetsUrl || '',
        isChat: window.wpAiWorkflowsSettings?.isChat || false,
        isViewer: window.wpAiWorkflowsSettings?.isViewer || false // Add viewer flag
    };
};

const settings = getSettings();

// Initialize based on context
if (settings.isChat) {
    const initializeChatWidget = async (container) => {
        const workflowId = container.dataset.workflowId;
        
        // IMPORTANT: Try to get existing config from the container first
        let existingConfig = null;
        if (container.dataset.config) {
            try {
                const decodedConfig = atob(container.dataset.config);
                existingConfig = JSON.parse(decodedConfig);
            } catch (error) {
                console.error("Error parsing existing config:", error);
            }
        }
        
        try {
            // Fetch latest config from backend if no existing config
            let config = existingConfig;
            
            if (!config) {
                const response = await fetch(
                    `${settings.apiUrl}/chat-config/${workflowId}`, {
                        headers: {
                            'X-WP-Nonce': settings.nonce
                        }
                    }
                );
                
                if (!response.ok) {
                    throw new Error('Failed to fetch chat config');
                }
                
                const data = await response.json();
                config = data.config;
            }
            
            const root = createRoot(container);
            root.render(
                <ChatWidget 
                    workflowId={workflowId}
                    config={config}
                    settings={settings}
                />
            );
        } catch (error) {
            console.error('Error initializing chat widget:', error);
        }
    };
 
    const initializeChatWidgets = () => {
        // Initialize containers with class
        document.querySelectorAll('.wp-ai-workflows-chat-container').forEach(container => {
            initializeChatWidget(container);
        });
 
        // Initialize containers with data attribute
        document.querySelectorAll('[data-wp-ai-chat]').forEach(container => {
            initializeChatWidget(container);
        });
    };
 
    // Add global initialization function
    window.wpAiWorkflowsChat = {
        initChat: (containerId) => {
            const container = document.getElementById(containerId);
            if (container) {
                initializeChatWidget(container);
            }
        }
    };
 
    // Initialize chat widgets
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeChatWidgets);
    } else {
        initializeChatWidgets();
    }
} else if (settings.isViewer) { // Add workflow viewer initialization
    // Global variable for mobile detection
    window.wpAiWorkflowsMobile = window.wpAiWorkflowsMobile || 
        (window.innerWidth < 768 || 
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    
    const initializeWorkflowViewer = (container) => {
        try {
            // Check if this viewer should be hidden on mobile
            const configData = container.dataset.config;
            if (!configData) {
                throw new Error('No configuration data available');
            }
            
            let config;
            try {
                const decodedConfig = atob(configData);
                config = JSON.parse(decodedConfig);
            } catch (parseError) {
                throw new Error(`Configuration parse error: ${parseError.message}`);
            }
            
            // Early exit if on mobile and hideOnMobile is true
            // This prevents parsing and loading the full workflow data
            if (window.wpAiWorkflowsMobile && config.hideOnMobile) {
                console.log('Skipping workflow viewer initialization on mobile device');
                // Container is already hidden by the PHP script
                return;
            }

            // Validate workflow data
            if (!config.workflowData || !config.workflowData.nodes || !config.workflowData.edges) {
                throw new Error('Invalid workflow data structure');
            }

            // Initialize React container
            const root = createRoot(container);

            // Remove loading indicator
            container.innerHTML = '';

            // Render the viewer with the new options
            root.render(
                <React.StrictMode>
                    <WorkflowViewer
                        workflowData={config.workflowData}
                        height={config.height || '500px'}
                        darkMode={config.darkMode || false}
                        showMinimap={config.showMinimap !== false}
                        showControls={config.showControls !== false}
                        nodesDraggable={config.nodesDraggable || false}
                        nodesConnectable={config.nodesConnectable || false}
                        elementsSelectable={config.elementsSelectable || false}
                        showSidebar={config.showSidebar || false}
                        allowPanZoom={config.allowPanZoom !== false}
                        showToolbar={config.showToolbar || false}
                        allowDownload={config.allowDownload || false}
                        hideOnMobile={config.hideOnMobile || false}
                        hidePoweredBy={config.hidePoweredBy || false}
                    />
                </React.StrictMode>
            );

        } catch (error) {
            // Only show error if we're not on mobile with hideOnMobile
            // This prevents showing errors for mobile devices where we intentionally don't load
            if (!(window.wpAiWorkflowsMobile && container.dataset.config)) {
                container.innerHTML = `
                    <div class="workflow-viewer-error" style="padding: 20px; text-align: center;">
                        <div style="color: #ff4d4f; margin-bottom: 10px;">⚠️ Error loading workflow viewer</div>
                        <div style="color: #666; font-size: 14px;">${error.message}</div>
                        <div style="color: #999; font-size: 12px; margin-top: 8px;">
                            Check browser console for more details
                        </div>
                    </div>
                `;
            }
            console.error('Workflow viewer initialization error:', error);
        }
    };

    const initializeAllViewers = () => {
        const containers = document.querySelectorAll('.wp-ai-workflows-viewer-container');
        
        containers.forEach((container, index) => {
            // Only initialize if not hidden by mobile detection
            // This check prevents even attempting to initialize viewers that shouldn't be shown
            const isHidden = container.style.display === 'none';
            if (!isHidden) {
                initializeWorkflowViewer(container);
            }
        });
    };

    // Global initialization function
    window.wpAiWorkflowsViewer = {
        init: (containerId) => {
            const container = document.getElementById(containerId);
            if (container) {
                // Only initialize if we should show this workflow on the current device
                const configData = container.dataset.config;
                if (configData) {
                    try {
                        const config = JSON.parse(atob(configData));
                        if (!(window.wpAiWorkflowsMobile && config.hideOnMobile)) {
                            initializeWorkflowViewer(container);
                        }
                    } catch (e) {
                        console.error('Error parsing viewer config:', e);
                    }
                } else {
                    initializeWorkflowViewer(container);
                }
            } else {
                console.error(`Container #${containerId} not found`);
            }
        }
    };

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeAllViewers);
    } else {
        initializeAllViewers();
    }
} else {
    // Admin app initialization
    const adminRoot = document.getElementById('wp-ai-workflows-root');
    if (adminRoot) {
        const root = createRoot(adminRoot);
        root.render(
            <React.StrictMode>
                <App />
            </React.StrictMode>
        );
        setupAdminInstance();
    }
}

reportWebVitals();